import { Injectable } from '@angular/core';
import { Observable, forkJoin, of } from 'rxjs';
import {map, catchError, mergeMap} from 'rxjs/operators';

export interface ValidationRule {
  alias: string;
  possibleValues: string[];
}

export interface ValidationConfiguration {
  rules: ValidationRule[];
  ignoredAliases?: string[];
  nodeSpecificIgnores?: { [nodeId: number]: string[] };
}

export interface ModuleValidationConfig {
  [moduleCode: string]: ValidationConfiguration;
}

export interface ValidationResult {
  canProgress: boolean;
  blockingConditions: string[];
  warnings: string[];
  analysisDetails: {
    totalCombinations: number;
    validCombinations: number;
    blockedCombinations: number;
  };
  transitionAnalysis: TransitionAnalysis[];
}

export interface TransitionAnalysis {
  transitionName: string;
  canProgress: boolean;
  conditions: ParsedCondition[];
  validValues: { [alias: string]: string[] };
  blockedValues: { [alias: string]: string[] };
  warnings?: string[];
}

export interface ParsedCondition {
  propertyName: string;
  operator: string;
  value: string;
  operatorType: 'equality' | 'set' | 'comparison' | 'other';
}

@Injectable({
  providedIn: 'root'
})
export class NodeValidationService {
  private moduleService: any;
  private tenantService: any;
  private currentModule: string = 'AI';
  private useDynamicRules: boolean = true; // Flag to enable dynamic rule loading
  private dynamicRulesCache: Map<string, ValidationConfiguration> = new Map();
  
  // Cache for list items to avoid repeated AIRSWEBLIST requests
  private listItemsCache: Map<string, any[]> = new Map();
  
  // Cache for field metadata to support lazy loading
  private fieldMetadataCache: Map<string, any> = new Map();
  private moduleConfigs: ModuleValidationConfig = {
    'AI': {
      rules: [],
      ignoredAliases: [],
      nodeSpecificIgnores: {
        // To-do figure if these need to be hardcoded for each module.
        2800: ['ClaimStatusAlias'],
        2020: ['InvestigationSignOff'],
        2030: ['SignOff1SignOff'],
        2040: ['SignOff2SignOff'],
        2015: ['TriageSignOff', 'TriageRequiredAlias']
      }
    },
    // Add default empty configs for other modules that can be overridden by dynamic loading
    'Risk': {
      rules: [],
      ignoredAliases: [],
      nodeSpecificIgnores: {}
    },
    'Register': {
      rules: [],
      ignoredAliases: [],
      nodeSpecificIgnores: {}
    },
    'PermitToWork': {
      rules: [],
      ignoredAliases: [],
      nodeSpecificIgnores: {}
    },
    'TallyIndex': {
      rules: [],
      ignoredAliases: [],
      nodeSpecificIgnores: {}
    }
  };

  private operatorMap: { [key: number]: string } = {
    0: 'IsEqualTo',
    1: 'NotEqualTo',
    2: 'IsGreaterThan',
    3: 'IsGreaterThanOrEqualTo',
    4: 'IsLessThan',
    5: 'IsLessThanOrEqualTo',
    6: 'IsAfterDate',
    7: 'IsBeforeDate',
    8: 'NowHasValue',
    9: 'NoLongerHasValue',
    10: 'AllAreIn',
    11: 'AllAreNotIn',
    12: 'AnyAreIn',
    13: 'AnyAreNotIn',
    14: 'IsChildOf',
    15: 'IsNotChildOf'
  };

  setCurrentModule(moduleCode: string) {
    this.currentModule = moduleCode;
  }

  // Inject services from components that have access to them
  setServices(moduleService: any, tenantService: any) {
    this.moduleService = moduleService;
    this.tenantService = tenantService;
  }

  // Clear dynamic rules cache
  clearCache() {
    this.dynamicRulesCache.clear();
    this.listItemsCache.clear();
    this.fieldMetadataCache.clear();
  }

  loadList(listName: string): Observable<any> {
    return this.tenantService.getListItems(["AIRSWEBLIST", listName]).pipe(
        mergeMap((r) => {
          // Cache the result and return it
          return of(r);
        })
    );
  }

  // Asynchronously load validation rules from module field definitions
  async loadDynamicValidationConfig(moduleCode?: string): Promise<ValidationConfiguration> {
    const module = moduleCode || this.currentModule;
    
    if (!this.moduleService || !this.tenantService) {
      return this.getValidationConfigStatic(module);
    }

    try {
      
      // Get all fields from the module
      const moduleFields = this.moduleService.getModuleFields();
      
      // Filter fields that can have validation rules
      // Include fields with ListAlias OR fields that are dropdowns/lists
      const fieldsWithLists = moduleFields.filter((field: any) => {
        const hasListAlias = field.ListAlias && field.ListAlias.trim() !== '';
        const isListField = field.FieldType === 'Dropdown' || 
                           field.FieldType === 'List' || 
                           field.FieldType === 'SingleSelect' ||
                           field.FieldType === 'MultiSelect';
        const isCheckbox = field.FieldType === 'Checkbox' || field.FieldType === 'Boolean';
        const isSeverityMatrix = field.FieldType === 'severitymatrix';
        
        return hasListAlias || isListField || isCheckbox || isSeverityMatrix;
      });

      // Create validation rules with lazy loading - don't fetch values upfront
      const validationRules: ValidationRule[] = [];
      
      for (const field of fieldsWithLists) {
        try {
          let possibleValues: string[] = [];
          // Handle checkbox/boolean fields specially - these don't need API calls
          if (field.FieldType === 'Checkbox' || field.FieldType === 'Boolean') {
            possibleValues = ['true', 'false'];
          } else {
            // For list fields, create rules without values initially - they'll be loaded on demand
            possibleValues = []; // Empty - will be populated lazily
          }
          
          validationRules.push({
            alias: field.FieldId, // Use FieldId as the alias
            possibleValues: possibleValues
          });
          
          // Store field metadata for lazy loading
          this.storeFieldMetadata(field.FieldId, field);
          
        } catch (error) {
          console.warn(`Failed to process field ${field.FieldId}:`, error);
        }
      }
      
      // Create the dynamic configuration
      const dynamicConfig: ValidationConfiguration = {
        rules: validationRules,
        ignoredAliases: [],
        nodeSpecificIgnores: this.getDefaultNodeSpecificIgnores(module)
      };
      
      // Cache the result
      this.dynamicRulesCache.set(module, dynamicConfig);
      
      return dynamicConfig;
      
    } catch (error) {
      return this.getValidationConfigStatic(module);
    }
  }

  private extractPossibleValues(listItems: any[]): string[] {
    if (!listItems || listItems.length === 0) {
      return [];
    }
    
    return listItems.map((item: any) => {
      const value = item.Value || 
             item.Code || 
             item.Name || 
             item.Text ||
             item.Key ||
             item.Id ||
             (typeof item === 'string' ? item : null) ||
             item.toString?.();
      return value;
    }).filter((value: any) => value != null && value !== '[object Object]');
  }

  private storeFieldMetadata(fieldId: string, field: any) {
    this.fieldMetadataCache.set(fieldId, field);
  }

  // Lazy load list values for a specific field when needed
  private async loadFieldValuesOnDemand(alias: string): Promise<string[]> {
    const field = this.fieldMetadataCache.get(alias);
    if (!field) {
      return [];
    }

    try {
      let possibleValues: string[] = [];
      
      if (field.FieldType === 'severitymatrix') {
        const listItems = await this.getFieldListItems('sevmatrix_default');
        possibleValues = this.extractPossibleValues(listItems);
      }
      else if (field.ListAlias && field.ListAlias.trim() !== '') {
        const listItems = await this.getFieldListItems(field.ListAlias);
        possibleValues = this.extractPossibleValues(listItems);
      }
      else if (field.FieldType === 'Dropdown' || field.FieldType === 'List') {
        const listItems = await this.getFieldListItems(field.FieldId);
        possibleValues = this.extractPossibleValues(listItems);
      }
      
      return possibleValues;
    } catch (error) {
      console.warn(`Failed to load values for field ${alias}:`, error);
      return [];
    }
  }

  private async getFieldListItems(listAlias: string): Promise<any[]> {
    // Check cache first
    if (this.listItemsCache.has(listAlias)) {
      return this.listItemsCache.get(listAlias)!;
    }

    return new Promise((resolve, reject) => {
      
      this.tenantService
        .getListItems(['AIRSWEBLIST', listAlias])
        .subscribe(
          (items: any[]) => {
            const result = items || [];
            // Cache the result
            this.listItemsCache.set(listAlias, result);
            resolve(result);
          },
          (error: any) => {
            reject(error);
          }
        );
    });
  }

  private getValidationConfigStatic(moduleCode: string): ValidationConfiguration {
    const configs = this.moduleConfigs;
    return configs[moduleCode] || {
      rules: [],
      ignoredAliases: [],
      nodeSpecificIgnores: {}
    };
  }

  private getDefaultNodeSpecificIgnores(moduleCode: string): { [nodeId: number]: string[] } {
    // Return the hardcoded node-specific ignores for now - these could also be made dynamic in the future
    const staticConfig = this.getValidationConfigStatic(moduleCode);
    return staticConfig.nodeSpecificIgnores || {};
  }

  getCurrentModule(): string {
    return this.currentModule;
  }

  getValidationConfig(moduleCode?: string): ValidationConfiguration {
    const module = moduleCode || this.currentModule;
    
    // Try to get dynamic rules first if enabled and services are available
    if (this.useDynamicRules && this.moduleService && this.tenantService) {
      const cached = this.dynamicRulesCache.get(module);
      if (cached) {
        return cached;
      }
      
      // If not cached, load dynamically (this will be async, so we'll need to handle this differently)
      // For now, fall back to static rules but log that we should load dynamically
    }
    
    // Fall back to static configuration
    const configs = this.moduleConfigs;
    return configs[module] || {
      rules: [],
      ignoredAliases: [],
      nodeSpecificIgnores: {}
    };
  }

  updateValidationConfig(config: ValidationConfiguration, moduleCode?: string) {
    const module = moduleCode || this.currentModule;
    const configs = this.moduleConfigs;
    this.moduleConfigs = {
      ...configs,
      [module]: config
    };
  }

  setModuleConfig(moduleCode: string, config: ValidationConfiguration) {
    const configs = this.moduleConfigs;
    this.moduleConfigs = {
      ...configs,
      [moduleCode]: config
    };
  }

  getAllModuleConfigs(): ModuleValidationConfig {
    return this.moduleConfigs;
  }

  async validateNodeTransitionsSeparately(transitionsWithRules: Array<{edge: any, rules: any[]}>, nodeId: number, moduleStatusId: number, moduleCode?: string): Promise<ValidationResult> {
    if (transitionsWithRules.length === 0) {
      return {
        canProgress: false,
        blockingConditions: ['No visible outgoing transitions found (check edge filters)'],
        warnings: [],
        analysisDetails: {
          totalCombinations: 0,
          validCombinations: 0,
          blockedCombinations: 0
        },
        transitionAnalysis: []
      };
    }

    const transitionAnalyses: TransitionAnalysis[] = [];
    let hasValidTransition = false;
    const allWarnings: string[] = [];

    // Analyze each transition separately
    for (const transitionData of transitionsWithRules) {
      const edge = transitionData.edge;
      const systemRules = transitionData.rules.filter(rule => rule.RuleType === 'SystemRule');

      const transitionName = edge.Name || edge.label || `Transition ${edge.Id}`;

      if (systemRules.length === 0) {
        // No system rules means this transition can always progress
        hasValidTransition = true;
        transitionAnalyses.push({
          transitionName,
          canProgress: true,
          conditions: [],
          validValues: {},
          blockedValues: {}
        });
        continue;
      }

      const conditions: ParsedCondition[] = [];

      // Parse all conditions for this transition
      for (const rule of systemRules) {
        if (rule.FullRuleJson) {
          try {
            const jsonData = JSON.parse(rule.FullRuleJson);
            if (jsonData.Conditions && jsonData.Conditions.length > 0) {
              const ruleConditions = jsonData.Conditions.map((condition: any) => ({
                propertyName: condition.PropertyName,
                operator: this.operatorMap[condition.Operator] || `Unknown(${condition.Operator})`,
                value: condition.Value,
                operatorType: this.getOperatorType(condition.Operator)
              }));
              conditions.push(...ruleConditions);
            }
          } catch (e) {
            console.warn('Failed to parse rule JSON:', e);
          }
        }
      }

      // Analyze this specific transition
      const transitionResult = await this.analyzeTransitionConditions(conditions, transitionName, nodeId, moduleStatusId, moduleCode);
      transitionAnalyses.push(transitionResult);

      if (transitionResult.canProgress) {
        hasValidTransition = true;
      }

      allWarnings.push(...transitionResult.warnings || []);
    }

    // Calculate overall statistics
    const validTransitions = transitionAnalyses.filter(t => t.canProgress).length;
    const totalTransitions = transitionAnalyses.length;

    const blockingConditions: string[] = [];

    // Comprehensive coverage analysis: Check if ALL possible alias values have at least one valid path
    const coverageAnalysis = await this.analyzeCoverageForAllAliases(transitionAnalyses, nodeId, moduleCode);
    const hasFullCoverage = Object.values(coverageAnalysis.uncoveredValues).every(uncoveredArray => uncoveredArray.length === 0);


    // Update hasValidTransition based on coverage analysis
    hasValidTransition = hasValidTransition && hasFullCoverage;

    if (!hasValidTransition) {
      if (validTransitions === 0) {
        blockingConditions.push(`All ${totalTransitions} transition(s) are blocked by system rules`);
      } else if (!hasFullCoverage) {
        blockingConditions.push('Some alias values have no valid transition paths');

        // Add specific uncovered values
        for (const [alias, uncoveredValues] of Object.entries(coverageAnalysis.uncoveredValues)) {
          if (uncoveredValues.length > 0) {
            blockingConditions.push(`${alias}: No path for values [${uncoveredValues.join(', ')}]`);
          }
        }
      }

      // Add individual transition blocking details
      transitionAnalyses.forEach(analysis => {
        if (!analysis.canProgress) {
          const blockedAliases = Object.keys(analysis.blockedValues);
          if (blockedAliases.length > 0) {
            blockingConditions.push(`${analysis.transitionName}: Blocked by ${blockedAliases.join(', ')}`);
          }
        }
      });
    }

    return {
      canProgress: hasValidTransition,
      blockingConditions,
      warnings: [...new Set(allWarnings)], // Remove duplicates
      analysisDetails: {
        totalCombinations: totalTransitions,
        validCombinations: validTransitions,
        blockedCombinations: totalTransitions - validTransitions
      },
      transitionAnalysis: transitionAnalyses
    };
  }

  validateNodeTransitions(edgeRules: any[]): ValidationResult {
    const systemRules = edgeRules.filter(rule => rule.RuleType === 'SystemRule');

    if (systemRules.length === 0) {
      return {
        canProgress: true,
        blockingConditions: [],
        warnings: [],
        analysisDetails: {
          totalCombinations: 1,
          validCombinations: 1,
          blockedCombinations: 0
        },
        transitionAnalysis: []
      };
    }

    const allConditions: ParsedCondition[] = [];

    for (const rule of systemRules) {
      if (rule.FullRuleJson) {
        try {
          const jsonData = JSON.parse(rule.FullRuleJson);
          if (jsonData.Conditions && jsonData.Conditions.length > 0) {
            const ruleConditions = jsonData.Conditions.map((condition: any) => ({
              propertyName: condition.PropertyName,
              operator: this.operatorMap[condition.Operator] || `Unknown(${condition.Operator})`,
              value: condition.Value,
              operatorType: this.getOperatorType(condition.Operator)
            }));
            allConditions.push(...ruleConditions);
          }
        } catch (e) {
          console.warn('Failed to parse rule JSON:', e);
        }
      }
    }

    return this.analyzeConditions(allConditions);
  }

  private async analyzeTransitionConditions(conditions: ParsedCondition[], transitionName: string, nodeId?: number, moduleStatusId?: number, moduleCode?: string): Promise<TransitionAnalysis> {
    const config = this.getValidationConfig(moduleCode);
    const validValues: { [alias: string]: string[] } = {};
    const blockedValues: { [alias: string]: string[] } = {};
    const warnings: string[] = [];

    if (conditions.length === 0) {
      return {
        transitionName,
        canProgress: true,
        conditions,
        validValues,
        blockedValues,
        warnings
      };
    }

    // Group conditions by property name
    const conditionsByProperty = new Map<string, ParsedCondition[]>();
    for (const condition of conditions) {
      if (!conditionsByProperty.has(condition.propertyName)) {
        conditionsByProperty.set(condition.propertyName, []);
      }
      conditionsByProperty.get(condition.propertyName)!.push(condition);
    }

    let transitionCanProgress = true;

    // Analyze each property's conditions for this transition
    for (const [propertyName, propertyConditions] of conditionsByProperty) {
      const rule = config.rules.find(r => r.alias === propertyName);

      if (!rule) {
        warnings.push(`Unknown property: ${propertyName} - cannot validate`);
        continue;
      }

      // Check both global and node-specific ignores
      const globalIgnore = config.ignoredAliases?.includes(propertyName);

      const nodeSpecificIgnore = moduleStatusId && config.nodeSpecificIgnores && config.nodeSpecificIgnores[moduleStatusId]?.includes(propertyName);

      if (globalIgnore || nodeSpecificIgnore) {
        const reason = nodeSpecificIgnore ? `ignored for status ${moduleStatusId}` : 'globally ignored';
        warnings.push(`Ignoring property: ${propertyName} (${reason})`);
        continue;
      }

      // Lazy load values if they're empty (except for boolean fields which are pre-populated)
      let propertyValues = rule.possibleValues;
      if (propertyValues.length === 0) {
        try {
          propertyValues = await this.loadFieldValuesOnDemand(propertyName);
          // Update the rule with loaded values for future use
          rule.possibleValues = propertyValues;
        } catch (error) {
          console.warn(`Failed to load values for property ${propertyName}:`, error);
          warnings.push(`Failed to load values for property: ${propertyName}`);
          continue;
        }
      }

      const validValuesForProperty: string[] = [];
      const blockedValuesForProperty: string[] = [];

      // Check each possible value against all conditions for this property
      for (const value of propertyValues) {
        let isValidForAllConditions = true;

        // For this transition, ALL conditions for this property must be satisfied (AND logic within property)
        for (const condition of propertyConditions) {
          if (!this.evaluateCondition(condition, value)) {
            isValidForAllConditions = false;
            break;
          }
        }

        if (isValidForAllConditions) {
          validValuesForProperty.push(value);
        } else {
          blockedValuesForProperty.push(value);
        }
      }

      validValues[propertyName] = validValuesForProperty;
      blockedValues[propertyName] = blockedValuesForProperty;

      // If no valid values exist for this property, this transition is blocked
      if (validValuesForProperty.length === 0) {
        transitionCanProgress = false;
      }
    }

    return {
      transitionName,
      canProgress: transitionCanProgress,
      conditions,
      validValues,
      blockedValues,
      warnings
    };
  }

  private analyzeConditions(conditions: ParsedCondition[], moduleCode?: string): ValidationResult {
    const config = this.getValidationConfig(moduleCode);
    const blockingConditions: string[] = [];
    const warnings: string[] = [];

    // Group conditions by property name
    const conditionsByProperty = new Map<string, ParsedCondition[]>();
    for (const condition of conditions) {
      if (!conditionsByProperty.has(condition.propertyName)) {
        conditionsByProperty.set(condition.propertyName, []);
      }
      conditionsByProperty.get(condition.propertyName)!.push(condition);
    }

    let totalCombinations = 1;
    let blockedCombinations = 0;

    // Analyze each property's conditions
    for (const [propertyName, propertyConditions] of conditionsByProperty) {
      const rule = config.rules.find(r => r.alias === propertyName);

      if (!rule) {
        warnings.push(`Unknown property: ${propertyName} - cannot validate`);
        continue;
      }

      if (config.ignoredAliases?.includes(propertyName)) {
        warnings.push(`Ignoring property: ${propertyName} (marked as ignored)`);
        continue;
      }

      const propertyValues = rule.possibleValues;
      totalCombinations *= propertyValues.length;

      // Check each possible value against all conditions for this property
      const validValues: string[] = [];
      const invalidValues: string[] = [];

      for (const value of propertyValues) {
        let isValidForAllConditions = true;

        for (const condition of propertyConditions) {
          if (!this.evaluateCondition(condition, value)) {
            isValidForAllConditions = false;
            break;
          }
        }

        if (isValidForAllConditions) {
          validValues.push(value);
        } else {
          invalidValues.push(value);
        }
      }

      if (validValues.length === 0) {
        blockingConditions.push(`${propertyName}: No valid values found - all combinations blocked`);
        blockedCombinations = totalCombinations; // All combinations blocked
      } else if (invalidValues.length > 0) {
        const blockedCount = invalidValues.length;
        const validCount = validValues.length;
        blockedCombinations += Math.floor((blockedCount / propertyValues.length) * totalCombinations);

        blockingConditions.push(`${propertyName}: ${blockedCount}/${propertyValues.length} values blocked (${invalidValues.join(', ')})`);
      }
    }

    const validCombinations = totalCombinations - blockedCombinations;

    return {
      canProgress: validCombinations > 0,
      blockingConditions,
      warnings,
      analysisDetails: {
        totalCombinations,
        validCombinations,
        blockedCombinations
      },
      transitionAnalysis: [] // Not used in the old method
    };
  }

  private evaluateCondition(condition: ParsedCondition, value: string): boolean {
    const { operator, value: conditionValue } = condition;

    switch (operator) {
      case 'IsEqualTo':
        // Check if conditionValue contains commas - if so, treat as "equal to any of these"
        if (conditionValue.includes(',')) {
          const equalValues = conditionValue.split(',').map(v => v.trim());
          return equalValues.includes(value);
        }
        return value === conditionValue;

      case 'NotEqualTo':
        // Check if conditionValue contains commas - if so, treat as "not equal to any of these"
        if (conditionValue.includes(',')) {
          const notAllowedValues = conditionValue.split(',').map(v => v.trim());
          return !notAllowedValues.includes(value);
        }
        return value !== conditionValue;

      case 'AnyAreIn':
        // Value should be in the comma-separated list
        const allowedValues = conditionValue.split(',').map(v => v.trim());
        return allowedValues.includes(value);

      case 'AnyAreNotIn':
        // Value should NOT be in the comma-separated list
        const disallowedValues = conditionValue.split(',').map(v => v.trim());
        return !disallowedValues.includes(value);

      case 'AllAreIn':
        // For single values, this is the same as AnyAreIn
        const allAllowedValues = conditionValue.split(',').map(v => v.trim());
        return allAllowedValues.includes(value);

      case 'AllAreNotIn':
        // For single values, this is the same as AnyAreNotIn
        const allDisallowedValues = conditionValue.split(',').map(v => v.trim());
        return !allDisallowedValues.includes(value);

      case 'IsChildOf':
        // Check if value is a child of any of the comma-separated parent values
        if (conditionValue.includes(',')) {
          const parentValues = conditionValue.split(',').map(v => v.trim());
          return parentValues.some(parent => value.startsWith(parent + '_') || value.includes('_' + parent));
        }
        return value.startsWith(conditionValue + '_') || value.includes('_' + conditionValue);

      case 'IsNotChildOf':
        // Check if value is NOT a child of any of the comma-separated parent values
        if (conditionValue.includes(',')) {
          const parentValues = conditionValue.split(',').map(v => v.trim());
          return !parentValues.some(parent => value.startsWith(parent + '_') || value.includes('_' + parent));
        }
        return !(value.startsWith(conditionValue + '_') || value.includes('_' + conditionValue));

      default:
        // For other operators (comparisons, dates, etc.), we can't easily validate without more context
        // Return true to avoid false positives
        return true;
    }
  }

  private getOperatorType(operatorId: number): 'equality' | 'set' | 'comparison' | 'other' {
    switch (operatorId) {
      case 0: // IsEqualTo
      case 1: // NotEqualTo
        return 'equality';

      case 10: // AllAreIn
      case 11: // AllAreNotIn
      case 12: // AnyAreIn
      case 13: // AnyAreNotIn
        return 'set';

      case 2: // IsGreaterThan
      case 3: // IsGreaterThanOrEqualTo
      case 4: // IsLessThan
      case 5: // IsLessThanOrEqualTo
      case 6: // IsAfterDate
      case 7: // IsBeforeDate
        return 'comparison';

      default:
        return 'other';
    }
  }

  // Helper method to add new validation rules
  addValidationRule(alias: string, possibleValues: string[], moduleCode?: string) {
    const module = moduleCode || this.currentModule;
    const config = this.getValidationConfig(module);
    const existingRuleIndex = config.rules.findIndex(r => r.alias === alias);

    if (existingRuleIndex >= 0) {
      config.rules[existingRuleIndex].possibleValues = possibleValues;
    } else {
      config.rules.push({ alias, possibleValues });
    }

    this.updateValidationConfig({ ...config }, module);
  }

  // Helper method to update ignored aliases
  updateIgnoredAliases(ignoredAliases: string[], moduleCode?: string) {
    const module = moduleCode || this.currentModule;
    const config = this.getValidationConfig(module);
    this.updateValidationConfig({ ...config, ignoredAliases }, module);
  }

  // Helper methods for node-specific ignores
  setNodeSpecificIgnores(nodeId: number, ignoredAliases: string[], moduleCode?: string) {
    const module = moduleCode || this.currentModule;
    const config = this.getValidationConfig(module);
    const nodeSpecificIgnores = { ...config.nodeSpecificIgnores };
    nodeSpecificIgnores[nodeId] = ignoredAliases;
    this.updateValidationConfig({ ...config, nodeSpecificIgnores }, module);
  }

  getNodeSpecificIgnores(nodeId: number, moduleCode?: string): string[] {
    const module = moduleCode || this.currentModule;
    const config = this.getValidationConfig(module);
    return config.nodeSpecificIgnores?.[nodeId] || [];
  }

  removeNodeSpecificIgnores(nodeId: number, moduleCode?: string) {
    const module = moduleCode || this.currentModule;
    const config = this.getValidationConfig(module);
    if (config.nodeSpecificIgnores) {
      const nodeSpecificIgnores = { ...config.nodeSpecificIgnores };
      delete nodeSpecificIgnores[nodeId];
      this.updateValidationConfig({ ...config, nodeSpecificIgnores }, module);
    }
  }

  // Analyze if all possible alias values are covered by at least one transition
  private async analyzeCoverageForAllAliases(transitionAnalyses: TransitionAnalysis[], nodeId?: number, moduleCode?: string): Promise<{
    coveredValues: { [alias: string]: string[] };
    uncoveredValues: { [alias: string]: string[] };
  }> {
    const config = this.getValidationConfig(moduleCode);
    const coveredValues: { [alias: string]: string[] } = {};
    const uncoveredValues: { [alias: string]: string[] } = {};

    // Get all aliases that appear in any transition
    const allAliases = new Set<string>();
    transitionAnalyses.forEach(analysis => {
      Object.keys(analysis.validValues).forEach(alias => allAliases.add(alias));
      Object.keys(analysis.blockedValues).forEach(alias => allAliases.add(alias));
    });

    // For each alias, check coverage across all transitions
    for (const alias of allAliases) {
      // Skip globally ignored aliases and node-specific ignored aliases
      const globalIgnore = config.ignoredAliases?.includes(alias);
      const nodeSpecificIgnore = nodeId && config.nodeSpecificIgnores?.[nodeId]?.includes(alias);

      if (globalIgnore || nodeSpecificIgnore) {
        continue;
      }

      const rule = config.rules.find(r => r.alias === alias);
      if (!rule) {
        continue;
      }

      // Lazy load values if they're empty
      let allPossibleValues = rule.possibleValues;
      if (allPossibleValues.length === 0) {
        try {
          allPossibleValues = await this.loadFieldValuesOnDemand(alias);
          // Update the rule with loaded values for future use
          rule.possibleValues = allPossibleValues;
        } catch (error) {
          console.warn(`Failed to load values for alias ${alias} during coverage analysis:`, error);
          continue;
        }
      }
      const covered = new Set<string>();

      // Collect all values that are valid in at least one transition
      transitionAnalyses.forEach(analysis => {
        if (analysis.validValues[alias]) {
          // If this transition has rules for this alias, use its valid values
          analysis.validValues[alias].forEach(value => covered.add(value));
        } else if (analysis.conditions.length === 0) {
          // If transition has no system rules at all, it's valid for all values
          allPossibleValues.forEach(value => covered.add(value));
        } else if (!analysis.conditions.some(c => c.propertyName === alias)) {
          // If transition has system rules but none mention this alias
          // Only consider this transition if it can actually progress (not blocked by other aliases)
          if (analysis.canProgress) {
            allPossibleValues.forEach(value => covered.add(value));
          }
        }
      });

      const coveredArray = Array.from(covered);
      const uncoveredArray = allPossibleValues.filter(value => !covered.has(value));

      coveredValues[alias] = coveredArray;
      uncoveredValues[alias] = uncoveredArray;
    }

    return { coveredValues, uncoveredValues };
  }
}
